/**
 * Значки двери в карточке цели: главный вход и доступность.
 *
 * ЗАЧЕМ. «Вход» без уточнений — половина ответа. Человеку на коляске, с
 * коляской детской или с тележкой важно знать про ступень и пандус ДО того,
 * как он туда дойдёт: вернуться от неудобной двери к удобной стоит дороже
 * всего маршрута.
 *
 * ПОЧЕМУ ЗНАЧКИ, А НЕ ТЕКСТ. Строка «пандус, автоматическая дверь, ширина 90
 * см» на карточке шириной с телефон занимает две строки и тонет. Значок с
 * подписью читается за взгляд — но подпись обязательна: цвет и форма не могут
 * быть единственным признаком (WCAG 1.4.1), а иконка без слова непонятна.
 *
 * ЧЕГО ЗДЕСЬ НЕТ. Мы не выдумываем доступность за владельца: неотмеченное не
 * показывается вовсе. «Нет данных» честнее, чем «нет пандуса».
 */

import type { I18nKey } from '@app/i18n'
import { tr } from '@app/i18n'

export interface DoorFacts {
  readonly primary: boolean
  readonly wheelchairRamp: boolean
  readonly stepFree: boolean
  readonly automatic: boolean
  readonly widthCm: number | null
}

/** Разбор attrs узла-двери. Всё необязательно: пустая дверь — обычная дверь. */
export function doorFactsOf(attrs: Readonly<Record<string, unknown>> | undefined): DoorFacts {
  const width = Number(attrs?.['door_width_cm'])
  return {
    primary: attrs?.['primary'] === true,
    wheelchairRamp: attrs?.['wheelchair_ramp'] === true,
    stepFree: attrs?.['step_free'] === true,
    automatic: attrs?.['automatic_door'] === true,
    widthCm: Number.isFinite(width) && width > 0 ? Math.round(width) : null,
  }
}

export function hasDoorBadges(facts: DoorFacts): boolean {
  return (
    facts.primary ||
    facts.wheelchairRamp ||
    facts.stepFree ||
    facts.automatic ||
    facts.widthCm !== null
  )
}

export function DoorBadges({ facts }: { facts: DoorFacts }) {
  if (!hasDoorBadges(facts)) return null
  return (
    <ul class="door-badges" aria-label={tr('door.badges')}>
      {facts.primary && <Badge glyph="★" labelKey="door.primary" tone="main" />}
      {facts.wheelchairRamp && <Badge glyph="♿" labelKey="door.ramp" tone="a11y" />}
      {facts.stepFree && <Badge glyph="⇥" labelKey="door.step_free" tone="a11y" />}
      {facts.automatic && <Badge glyph="⇄" labelKey="door.automatic" tone="a11y" />}
      {facts.widthCm !== null && (
        <li class="door-badge">
          <span aria-hidden="true">↔</span>
          {tr('door.width', { cm: String(facts.widthCm) })}
        </li>
      )}
    </ul>
  )
}

function Badge({
  glyph,
  labelKey,
  tone,
}: {
  glyph: string
  labelKey: I18nKey
  tone: 'main' | 'a11y'
}) {
  return (
    <li class={`door-badge door-badge--${tone}`}>
      {/* Значок скрыт от читалки: рядом стоит то же самое словами. */}
      <span aria-hidden="true">{glyph}</span>
      {tr(labelKey)}
    </li>
  )
}
